import { useEffect, useRef, useState } from 'react';
import { portalApi } from '@/lib/api';
import { fmtEdDate, fmtEdTime } from '@/lib/tz';
import { Bell, Check, CalendarClock, CalendarOff, Repeat, UserPlus } from 'lucide-react';

const ICONS = { SCHEDULE: CalendarClock, TIME_OFF: CalendarOff, SHIFT_SWAP: Repeat, OPEN_SHIFT: UserPlus };

function relTime(d) {
  return `${fmtEdDate(d, { month: 'short', day: 'numeric' })}, ${fmtEdTime(d)}`;
}

// Staff notification bell + dropdown panel. Polls every 30s for new schedule alerts.
export default function PortalNotifications() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = () => portalApi.get('/notifications/staff/me')
    .then(({ data }) => { setItems(data.notifications); setUnread(data.unreadCount); })
    .catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const markAll = async () => {
    try { await portalApi.post('/notifications/staff/read-all'); load(); } catch { /* noop */ }
  };
  const markOne = async (id) => {
    try { await portalApi.patch(`/notifications/staff/read/${id}`); load(); } catch { /* noop */ }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="portal-notifications-btn"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-full hover:bg-bone text-muted-ink hover:text-charcoal transition-colors"
        aria-label="Notifications"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span data-testid="portal-notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-saffron text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="portal-notif-panel" className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-2xl border border-border/50 shadow-lg z-30 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/40">
            <p className="font-heading text-sm font-700 text-charcoal">Notifications</p>
            {unread > 0 && (
              <button data-testid="portal-notif-readall" onClick={markAll} className="text-xs text-saffron hover:underline flex items-center gap-1"><Check size={13} /> Mark all read</button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-border/30">
            {items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted-ink" data-testid="portal-notif-empty">You&apos;re all caught up.</p>
            ) : items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              return (
                <button
                  key={n.id}
                  data-testid="portal-notif-item"
                  onClick={() => !n.read && markOne(n.id)}
                  className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-bone/60 transition-colors ${n.read ? '' : 'bg-saffron/5'}`}
                >
                  <div className={`shrink-0 mt-0.5 ${n.read ? 'text-muted-ink' : 'text-saffron'}`}><Icon size={16} /></div>
                  <div className="min-w-0">
                    <p className={`text-sm ${n.read ? 'text-charcoal' : 'font-semibold text-charcoal'}`}>{n.title}</p>
                    <p className="text-xs text-muted-ink">{n.message}</p>
                    <p className="text-[10px] text-muted-ink/70 mt-0.5">{relTime(n.createdAt)}</p>
                  </div>
                  {!n.read && <span className="ml-auto w-2 h-2 rounded-full bg-saffron shrink-0 mt-1.5" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
